import { Link, useLocation } from "react-router-dom";
import { LogOut, Sparkles, RotateCcw } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import { DemoTutorialProvider, useDemoTutorial } from "@/components/DemoTutorial";

const NAV = [
  { to: (pid) => `/demo/dashboard?persona=${pid}`, label: "Feed", match: "/demo/dashboard" },
  { to: (pid) => `/demo/sent?persona=${pid}`, label: "Sent", match: "/demo/sent" },
  { to: (pid) => `/demo/profile?persona=${pid}`, label: "Profile", match: "/demo/profile" },
  { to: () => `/demo`, label: "Demo", match: "/demo", exact: true },
];

export default function DemoLayout({ children, persona }) {
  return (
    <DemoTutorialProvider>
      <DemoLayoutInner persona={persona}>{children}</DemoLayoutInner>
    </DemoTutorialProvider>
  );
}

function DemoLayoutInner({ children, persona }) {
  const loc = useLocation();
  const { restart } = useDemoTutorial();
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="demo-layout">
      <div className="bg-primary text-primary-foreground border-b border-primary/40" data-testid="demo-banner">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-mono">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            <span>DEMO MODE</span>
            <span className="opacity-70 hidden sm:inline normal-case tracking-normal">·</span>
            <span className="opacity-80 normal-case tracking-normal text-[11px] hidden sm:inline">
              Pre-baked tailoring for two example personas. Exit any time.
            </span>
          </div>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-mono hover:underline underline-offset-4" data-testid="demo-exit-link">
            Exit demo <LogOut className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-end max-w-[1600px] mx-auto px-5 md:px-12 py-1.5 border-b border-foreground/10 bg-background/60">
        <button
          onClick={restart}
          data-testid="demo-tutorial-restart"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-mono text-muted-foreground hover:text-primary transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Restart guide
        </button>
      </div>

      <header className="border-b border-foreground/20 glass-strong sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Link to={persona ? `/demo/dashboard?persona=${persona.id}` : "/demo"} className="inline-flex items-center gap-3">
            <Logo />
          </Link>
          {persona && (
            <nav className="hidden md:flex items-center gap-0 lg:gap-1 mr-auto ml-10">
              {NAV.map((n) => {
                const to = n.to(persona.id);
                const active = n.exact ? loc.pathname === n.match : loc.pathname.startsWith(n.match);
                return (
                  <Link
                    key={n.label}
                    to={to}
                    data-testid={`demo-nav-${n.label.toLowerCase()}`}
                    className={`px-3 lg:px-4 py-2 text-[10px] tracking-[0.2em] lg:tracking-[0.25em] uppercase font-mono transition-colors ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          )}
          <div className="flex items-center gap-3">
            {persona && (
              <span className="hidden lg:inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-mono text-muted-foreground border border-foreground/20 px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                <span>{persona.name}</span>
                <span className="opacity-60">· DEMO · ∞</span>
              </span>
            )}
            <ThemeToggle />
            <Link to="/auth?mode=signup" data-testid="demo-signup-btn" className="btn-primary inline-flex items-center gap-2 text-[11px]">
              Sign up free
            </Link>
          </div>
        </div>
        {/* Mobile nav - pills row below the header, only when a persona is selected. */}
        {persona && (
          <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-3 py-2 border-t border-foreground/15 bg-background/60" data-testid="demo-mobile-nav" style={{ scrollbarWidth: "none" }}>
            {NAV.map((n) => {
              const to = n.to(persona.id);
              const active = n.exact ? loc.pathname === n.match : loc.pathname.startsWith(n.match);
              return (
                <Link
                  key={n.label}
                  to={to}
                  data-testid={`demo-mnav-${n.label.toLowerCase()}`}
                  className={`shrink-0 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-mono border transition-colors ${active ? "text-primary border-primary bg-primary/5" : "text-muted-foreground border-foreground/15 hover:text-foreground"}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>
      <SiteFooter variant="site" />
    </div>
  );
}
